import { useEffect, useMemo, useState } from 'react'
import { useOutletContext } from 'react-router'
import { PageFooter } from '@/components/commons/page-footer'
import { PageHero } from '@/components/commons/page-hero'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { ArrowLeftIcon, CheckIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { AiManualRetouchingSpaceImages } from '@/features/photoset/components/ai-manual-retouching-space-images/ai-manual-retouching-space-images'
import { ManualRetouchingErrorModal } from '@/features/photoset/components/manual-retouching-error-modal'
import { PhotosetGenerating } from '@/features/photoset/components/photoset-loading'
import { PhotosetSpaceElement } from '@/features/photoset/components/photoset-space-element'
import {
	PhotosetStepBodyContainer,
	PhotosetStepWrapper
} from '@/features/photoset/components/photoset-step-atoms'
import { StepHeroControls } from '@/features/photoset/components/step-hero-controls'
import { SubHeaderBanner } from '@/features/photoset/components/sub-header-banner'
import { useDeleteManualRetouchedImages } from '@/features/photoset/queries/use-delete-manual-retouched-images'
import { useGetNeedManualRetouchingSpaces } from '@/features/photoset/queries/use-get-need-manual-retouching-spaces'
import { useGetPhotoset } from '@/features/photoset/queries/use-get-photoset'
import { GET_PHOTOSET_IMAGES_LIST_RESPONSE_TYPE } from '@/features/photoset/queries/use-get-photoset-images-list'
import { useUpdateStepToAiVetting } from '@/features/photoset/queries/use-update-step-to-ai-vetting'
import { useUpdateStepToAiVettingCompleted } from '@/features/photoset/queries/use-update-step-to-ai-vetting-completed'
import type { PhotosetURLParams } from '@/features/photoset/types'
import { errorHandler } from '@/helpers/utils'
import { QUERY_KEYS, queryClient } from '@/query-client'
import { useTranslation } from '@/translations/i18n'

export const PhotosetAiManualRetouchingStep = () => {
	const { t } = useTranslation()
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const [hideManualRetouchingErrorModal, setHideManualRetouchingErrorModal] =
		useState(false)
	const [isGenerationThumbsCompleted, setIsGenerationThumbsCompleted] =
		useState(false)

	// queries
	const photosetQuery = useGetPhotoset(
		{ id: urlParams.id },
		{
			refetchInterval: () =>
				isGenerationThumbsCompleted ? false : 10_000
		}
	)
	const {
		data: photosetSpaces,
		isLoading: isLoadingManualRetouchingSpaces,
		isError: isErrorManualRetouchingSpaces,
		refetch: refetchPhotosetSpaces
	} = useGetNeedManualRetouchingSpaces(
		{
			urlParams: {
				photosetId: urlParams.id
			},
			params: {
				response_type: GET_PHOTOSET_IMAGES_LIST_RESPONSE_TYPE,
				is_final_select: true
			}
		},
		{ enabled: false }
	)

	// mutations
	const {
		mutate: deleteManualRetouchedImages,
		isPending: isPendingDeleteManualRetouchedImages
	} = useDeleteManualRetouchedImages({
		photosetId: urlParams.id,
		options: {
			onError: (error) => errorHandler(error),
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: [QUERY_KEYS.PHOTOSET]
				})
				queryClient.invalidateQueries({
					queryKey: [QUERY_KEYS.MANUAL_RETOUCHING_SPACES]
				})
			}
		}
	})

	const {
		mutate: updateStepToAiVettingCompleted,
		isPending: isPendingUpdateToAiVettingCompleted
	} = useUpdateStepToAiVettingCompleted({
		onError: (error) => errorHandler(error),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.PHOTOSET]
			})
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.PHOTOSET_SPACES]
			})
		}
	})

	const {
		mutate: updateStepToAiVetting,
		isPending: isPendingUpdateToAiVetting
	} = useUpdateStepToAiVetting({
		onError: (error) => errorHandler(error),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.PHOTOSET]
			})
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.PHOTOSET_SPACES]
			})
		}
	})

	const isPending = useMemo(
		() =>
			isPendingUpdateToAiVetting ||
			isPendingDeleteManualRetouchedImages ||
			isPendingUpdateToAiVettingCompleted,
		[
			isPendingUpdateToAiVetting,
			isPendingDeleteManualRetouchedImages,
			isPendingUpdateToAiVettingCompleted
		]
	)

	// booleans
	const isNotRetouchedImagesError = useMemo(
		() =>
			photosetSpaces && photosetQuery
				? photosetSpaces.flatMap((photosetSpace) =>
						photosetSpace.images.filter(
							(image) =>
								!image.is_manually_retouched &&
								image.need_manual_retouching
						)
					).length > 0
				: false,
		[photosetSpaces, photosetQuery]
	)

	useEffect(() => {
		if (photosetQuery?.data?.retouched_thumbnail_generated_at) {
			refetchPhotosetSpaces()
			setIsGenerationThumbsCompleted(true)
		}
	}, [
		photosetQuery?.data?.retouched_thumbnail_generated_at,
		refetchPhotosetSpaces
	])

	if (!isGenerationThumbsCompleted) {
		return (
			<PhotosetGenerating
				subtitle={t('common:generating_waiting_page_description')}
				title={t('common:generating_thumbnails')}
			/>
		)
	}

	return (
		<>
			<PageHero
				endSlot={<StepHeroControls hideSortControls />}
				subtitle={t('photoset:ai_manual_retouching.subtitle')}
				title={t('photoset:ai_manual_retouching.title')}
			/>

			{isNotRetouchedImagesError &&
				!isLoadingManualRetouchingSpaces &&
				!photosetQuery.isLoading && (
					<SubHeaderBanner
						subtitle={t(
							'photoset:ai_manual_retouching.error_banner_subtitle'
						)}
						title={t(
							'photoset:ai_manual_retouching.error_banner_title'
						)}
						type={'danger'}
					/>
				)}

			{isNotRetouchedImagesError &&
				!isLoadingManualRetouchingSpaces &&
				!photosetQuery.isLoading &&
				!hideManualRetouchingErrorModal && (
					<ManualRetouchingErrorModal
						onClose={() => setHideManualRetouchingErrorModal(true)}
					/>
				)}

			<PhotosetStepWrapper>
				<PhotosetStepBodyContainer>
					{isLoadingManualRetouchingSpaces &&
						[0, 1, 2].map((e) => (
							<PhotosetSpaceElement asSkeleton key={`${e}-id`} />
						))}
					{!(
						isLoadingManualRetouchingSpaces ||
						isErrorManualRetouchingSpaces
					) &&
						photosetSpaces?.map((photosetSpace) => {
							const manuallyRetouchedImagesLength =
								photosetSpace.images.filter(
									(image) => image.is_manually_retouched
								).length
							const selectedImagesForManualRetouchingLength =
								photosetSpace.images.filter(
									(image) => image.need_manual_retouching
								).length
							return (
								photosetSpace.images.length > 0 && (
									<PhotosetSpaceElement
										endSlot={
											<AiManualRetouchingSpaceImages
												images={photosetSpace.images}
												isLoadingManualRetouchingSpaces={
													isLoadingManualRetouchingSpaces
												}
											/>
										}
										key={photosetSpace.space.id}
										spaceHeaderEndSlot={[
											t('common:retouched_image', {
												count: manuallyRetouchedImagesLength
											}),
											t(
												'photoset:ai_manual_retouching.selected_image',
												{
													count: selectedImagesForManualRetouchingLength
												}
											)
										].join(', ')}
										spaceName={photosetSpace.space.key_name}
									/>
								)
							)
						})}
				</PhotosetStepBodyContainer>
				<PageFooter
					endSlot={
						<div className='flex gap-4'>
							<ButtonLegacy
								disabled={
									isPending || !isGenerationThumbsCompleted
								}
								onClick={() => deleteManualRetouchedImages()}
								type='button'
								variant='secondary'
							>
								{t('common:delete_uploaded_images')}
							</ButtonLegacy>
							<ButtonLegacy
								disabled={
									isNotRetouchedImagesError ||
									isPending ||
									!isGenerationThumbsCompleted
								}
								onClick={() =>
									updateStepToAiVettingCompleted(urlParams.id)
								}
								type='button'
								variant={
									isNotRetouchedImagesError && !isPending
										? 'primary'
										: 'primarySuccess'
								}
							>
								{isPendingUpdateToAiVettingCompleted && (
									<Spinner size={20} />
								)}
								{t('common:done')}
								<CheckIcon />
							</ButtonLegacy>
						</div>
					}
					startSlot={
						<ButtonLegacy
							disabled={isPending}
							onClick={() => updateStepToAiVetting(urlParams.id)}
							type='button'
							variant='tertiary'
						>
							<ArrowLeftIcon />
							{t('photoset:ai_vetting.title')}
						</ButtonLegacy>
					}
				/>
			</PhotosetStepWrapper>
		</>
	)
}
